import React, { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { collection, getDocs, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  CreditCard,
  Plus,
  ArrowLeft,
  Save,
  X,
  Upload,
  Image as ImageIcon,
  Trash2,
  Search,
  Eye,
  MoreVertical,
  Unlink
} from 'lucide-react'
import toast from 'react-hot-toast'

const AddSubscriptionCards = () => {
  const { subscriptionId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState(location.state?.subscription || null)
  const [loading, setLoading] = useState(!subscription)
  const [saving, setSaving] = useState(false)
  const [cards, setCards] = useState([])
  const [showAddCardModal, setShowAddCardModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingCardId, setEditingCardId] = useState(null)
  const [currentCard, setCurrentCard] = useState({
    collectionName: '',
    title: '',
    testCount: '',
    homeContentId: '', // Store the home content ID for copying
    discountPlan: '',
    index: '',
    image: null,
    imagePreview: ''
  })
  const [availableCollections, setAvailableCollections] = useState([])
  const [availableCards, setAvailableCards] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [cardSearchTerm, setCardSearchTerm] = useState('')
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false)
  const [showCardDropdown, setShowCardDropdown] = useState(false)
  const [isExistingCard, setIsExistingCard] = useState(false) // Track if selecting existing card
  const [activeMenuCardId, setActiveMenuCardId] = useState(null)

  const {
    getSubscriptionById,
    getSubscriptions,
    uploadImage,
    updateSubscription,
    getHomeContents,
    createSubscriptionCard,
    updateSubscriptionCard,
    removeSubscriptionCard,
    getHomeContentMeta
  } = useFirebase()

  useEffect(() => {
    if (subscriptionId) {
      loadSubscription()
    }
    loadCollections()
  }, [subscriptionId])

  // Reload available cards when subscription data changes (to filter out cards already added)
  useEffect(() => {
    if (subscription) {
      loadCollections()
    }
  }, [subscription?.cardIds])

  const loadSubscription = async () => {
    console.log('loadSubscription triggered for ID:', subscriptionId)
    try {
      setLoading(true)
      const data = await getSubscriptionById(subscriptionId)
      console.log('Loaded subscription data:', data)
      console.log('Card IDs to fetch:', data?.cardIds)

      if (data) {
        setSubscription(data)

        let allCards = []

        // STRICT LOGIC: Get cardIds and match the home/ node
        if (data.cardIds && data.cardIds.length > 0) {
          try {
            console.log('Fetching cards from home collection...')
            const cardsPromise = data.cardIds.map(async (cardId) => {
              // Fetch each card from home collection directly as requested
              try {
                const cardDocRef = doc(db, 'home', cardId.trim())
                const cardSnap = await getDoc(cardDocRef)

                if (cardSnap.exists()) {
                  const cardData = cardSnap.data()
                  return {
                    id: cardSnap.id,
                    ...cardData,
                    // Ensure title is set correctly from home doc
                    title: cardData.title || cardData.CollectionName || cardSnap.id,
                    // Map other fields
                    TestCount: cardData.TestCount || cardData.testCount || '0',
                    imageUrl: cardData.imageUrl || '',
                    discountPlan: cardData.discountPlan || ''
                  }
                }
                console.log(`Card ${cardId} not found in home collection`)
                return null
              } catch (e) {
                console.error(`Error fetching card ${cardId}:`, e)
                return null
              }
            })

            const results = await Promise.all(cardsPromise)
            allCards = results.filter(card => card !== null)
            console.log('Successfully fetched cards from home:', allCards)

          } catch (fetchError) {
            console.error('Error fetching cards from home:', fetchError)
          }
        }

        // Fallback: If no cardIds were successfully fetched from home, check if data.cards has anything
        if (allCards.length === 0 && data.cards && data.cards.length > 0) {
          allCards = data.cards
        }

        console.log('Total cards to display:', allCards.length)
        setCards(allCards)
      } else {
        toast.error('Subscription not found')
        navigate('/subscription')
      }
    } catch (error) {
      console.error('Error loading subscription:', error)
      toast.error('Failed to load subscription')
      navigate('/subscription')
    } finally {
      setLoading(false)
    }
  }

  const loadCollections = async () => {
    try {
      const subscriptions = await getSubscriptions()

      const allCollections = subscriptions.map(s => ({
        ...s,
        id: s.id,
        title: s.CollectionName || s.collectionName,
        sourceCollection: 'Subscription'
      }))

      console.log('Loaded Subscription packages:', allCollections.length)
      setAvailableCollections(allCollections)

      // Also load existing cards from home collection
      const homeRef = collection(db, 'home')
      const homeSnapshot = await getDocs(homeRef)
      const existingCards = homeSnapshot.docs
        .filter(doc => {
          const cardId = doc.id
          // Filter out cards that are already in this subscription
          return !subscription?.cardIds?.includes(cardId)
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          title: doc.data().title || doc.data().CollectionName || doc.id,
          TestCount: doc.data().TestCount || doc.data().testCount || '0'
        }))

      console.log('Loaded available cards:', existingCards.length)
      setAvailableCards(existingCards)

      if (allCollections.length === 0) {
        toast.error('No subscription packages found in database')
      }
    } catch (error) {
      console.error('Error loading collections:', error)
      toast.error('Failed to load packages')
    }
  }

  const handleAddCard = async () => {
    // Fetch next index
    let nextIndex = '1'
    try {
      const meta = await getHomeContentMeta()
      nextIndex = ((meta?.lastIndex || 0) + 1).toString()
    } catch (error) {
      console.warn('Could not fetch next index:', error)
    }

    setCurrentCard({
      collectionName: '',
      title: '',
      testCount: '',
      homeContentId: '',
      discountPlan: '',
      index: nextIndex,
      image: null,
      imagePreview: ''
    })
    setSearchTerm('')
    setCardSearchTerm('')
    setIsExistingCard(false)
    setIsEditing(false)
    setEditingCardId(null)
    setShowAddCardModal(true)
  }

  const handleEditCard = (card) => {
    setCurrentCard({
      collectionName: card.title || card.collectionName,
      title: card.title || card.collectionName,
      testCount: (card.TestCount || card.testCount || 0).toString(),
      homeContentId: card.copiedFrom || card.id,
      sourceCollection: 'home',
      discountPlan: card.discountPlan || '',
      index: card.index || '',
      image: null,
      imagePreview: card.imageUrl || ''
    })
    setSearchTerm(card.title || card.collectionName)
    setIsEditing(true)
    setEditingCardId(card.id)
    setShowAddCardModal(true)
  }

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCollectionDropdown && !event.target.closest('.collection-select-container')) {
        setShowCollectionDropdown(false)
      }
      if (activeMenuCardId && !event.target.closest('.card-action-menu')) {
        setActiveMenuCardId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showCollectionDropdown, activeMenuCardId])

  // Remove card from subscription only (unlink)
  const handleUnlinkCard = async (cardId) => {
    if (window.confirm('Remove this card from the subscription? The card will still exist in the database and can be re-added later.')) {
      try {
        setSaving(true)

        // Just remove the card ID from the subscription's cardIds array
        const subscriptionRef = doc(db, 'Subscription', subscriptionId)
        const subscriptionSnap = await getDoc(subscriptionRef)

        if (subscriptionSnap.exists()) {
          const currentData = subscriptionSnap.data()
          const updatedCardIds = (currentData.cardIds || []).filter(id => id !== cardId)

          await updateDoc(subscriptionRef, {
            cardIds: updatedCardIds,
            cardsCount: updatedCardIds.length
          })

          // Update local state
          setCards(cards.filter(card => card.id !== cardId))
          loadCollections() // Refresh available cards list
          toast.success('Card removed from subscription')
        }
      } catch (error) {
        console.error('Error unlinking card:', error)
        toast.error('Failed to remove card from subscription')
      } finally {
        setSaving(false)
      }
    }
  }

  // Delete card permanently from database
  const handleDeleteCard = async (cardId) => {
    if (window.confirm('⚠️ PERMANENTLY DELETE this card? This will remove the card from the database completely, including all its test data. This action cannot be undone!')) {
      try {
        setSaving(true)

        // Use the context function to delete the card completely
        await removeSubscriptionCard(subscriptionId, cardId)

        // Update local state
        setCards(cards.filter(card => card.id !== cardId))
        loadCollections() // Refresh available cards list
        toast.success('Card permanently deleted from database')
      } catch (error) {
        console.error('Error deleting card:', error)
        toast.error('Failed to delete card')
      } finally {
        setSaving(false)
      }
    }
  }

  const handleCollectionSelect = (collection) => {
    // Get test count from testCounts field (actual count from home content)
    const testCount = collection.testCounts || collection.tests?.length || 0

    // Debug log to verify test count
    console.log('Selected collection:', collection.title)
    console.log('Home content ID:', collection.id)
    console.log('Test count:', testCount)

    // Use the collection's existing image URL directly (no blob)
    const imageUrl = collection.imageUrl || ''

    setCurrentCard({
      collectionName: collection.title || collection.CollectionName,
      title: collection.title || collection.CollectionName, // Set card title defaulting to collection name
      testCount: testCount.toString(),
      homeContentId: collection.id, // Store for copying subcollections
      sourceCollection: collection.sourceCollection || 'home',
      discountPlan: collection.discountPlan || '',
      image: null, // No file selected, will use existing URL
      imagePreview: imageUrl // Use direct Firebase URL
    })
    setSearchTerm(collection.title || collection.CollectionName) // Ensure this updates the input display
    setShowCollectionDropdown(false)
    setIsExistingCard(false) // This is a new card from test package
    toast.success(`Selected: ${collection.title || collection.CollectionName} (${testCount} tests)`)
  }

  const handleCardSelect = (card) => {
    console.log('Selected existing card:', card.id)

    setCurrentCard({
      collectionName: card.title || card.CollectionName,
      title: card.title || card.CollectionName,
      testCount: card.TestCount || card.testCount || '0',
      homeContentId: card.id,
      sourceCollection: 'home',
      discountPlan: card.discountPlan || '',
      image: null,
      imagePreview: card.imageUrl || ''
    })
    setCardSearchTerm(card.title || card.CollectionName)
    setShowCardDropdown(false)
    setIsExistingCard(true) // This is an existing card
    toast.success(`Selected card: ${card.title || card.CollectionName}`)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        // Use FileReader for safe base64 preview instead of blob URL
        const reader = new FileReader()
        reader.onloadend = () => {
          setCurrentCard({
            ...currentCard,
            imagePreview: reader.result, // Base64 string, not blob URL
            image: file
          })
          toast.success('Image selected successfully!')
        }
        reader.readAsDataURL(file)
      } else {
        toast.error('Please upload an image file')
      }
    }
  }

  const removeCardImage = () => {
    setCurrentCard({
      ...currentCard,
      image: null,
      imagePreview: ''
    })
  }

  const handleSaveCard = async () => {
    if (!currentCard.collectionName.trim()) {
      toast.error('Please select a collection name')
      return
    }

    if (!currentCard.testCount) {
      toast.error('Please select a collection from the dropdown to fetch test count')
      return
    }

    if (!currentCard.homeContentId) {
      toast.error('Please select a collection from the dropdown')
      return
    }

    try {
      setSaving(true)

      // Check if this is an existing card being linked or a new card being created
      if (isExistingCard && currentCard.homeContentId) {
        // Link existing card - just add the card ID to subscription's cardIds
        toast.loading('Linking card to subscription...')

        const subscriptionRef = doc(db, 'Subscription', subscriptionId)
        const subscriptionSnap = await getDoc(subscriptionRef)

        if (subscriptionSnap.exists()) {
          const currentData = subscriptionSnap.data()
          const currentCardIds = currentData.cardIds || []

          // Check if card is already linked
          if (currentCardIds.includes(currentCard.homeContentId)) {
            toast.dismiss()
            toast.error('This card is already in the subscription')
            setSaving(false)
            return
          }

          // Add the card ID to the subscription
          await updateDoc(subscriptionRef, {
            cardIds: arrayUnion(currentCard.homeContentId),
            cardsCount: currentCardIds.length + 1
          })

          setSaving(false)
          setShowAddCardModal(false)
          toast.dismiss()
          toast.success('Card linked successfully!')
        }
      } else {
        // Create new card from test package
        // Use the imagePreview which is already a Firebase URL from the selected collection
        let imageUrl = currentCard.imagePreview || ''

        // Only upload if user manually selected a new image file
        if (currentCard.image && typeof currentCard.image === 'object') {
          imageUrl = await uploadImage(currentCard.image, `subscriptions/${subscriptionId}/cards`)
        }

        const cardTitle = currentCard.title.trim() || currentCard.collectionName.trim()

        // Call Context function to create or update card
        toast.loading(isEditing ? 'Updating card...' : 'Creating card and copying test data...')

        if (isEditing) {
          await updateSubscriptionCard(editingCardId, {
            title: cardTitle,
            imageUrl: imageUrl,
            discountPlan: currentCard.discountPlan
          })
        } else {
          await createSubscriptionCard(subscriptionId, {
            title: cardTitle,
            imageUrl: imageUrl,
            TestCount: currentCard.testCount,
            homeContentId: currentCard.homeContentId,
            sourceCollection: currentCard.sourceCollection || 'home',
            discountPlan: currentCard.discountPlan,
            index: currentCard.index,
            currentCardCount: cards.length
          })
        }

        setSaving(false)
        setShowAddCardModal(false)
        toast.dismiss()
        toast.success(isEditing ? 'Card updated successfully!' : 'Card added successfully!')
      }

      // Reload subscription to see new card
      loadSubscription()

      // Reset current card
      setCurrentCard({
        collectionName: '',
        title: '',
        testCount: '',
        homeContentId: '',
        discountPlan: '',
        image: null,
        imagePreview: ''
      })
      setCardSearchTerm('')
      setIsExistingCard(false)
      setIsEditing(false)
      setEditingCardId(null)
      setSearchTerm('')
    } catch (error) {
      console.error('Error adding card:', error)
      toast.dismiss()
      toast.error('Failed to add card')
      setSaving(false)
    }
  }

  const handleCancelCard = () => {
    setShowAddCardModal(false)
    setCurrentCard({
      collectionName: '',
      title: '',
      testCount: '',
      homeContentId: '',
      discountPlan: '',
      image: null,
      imagePreview: ''
    })
    setSearchTerm('')
    setCardSearchTerm('')
    setIsExistingCard(false)
  }

  const filteredCollections = availableCollections.filter(collection =>
    collection.title?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredCards = availableCards.filter(card =>
    (card.title || card.CollectionName || '')?.toLowerCase().includes(cardSearchTerm.toLowerCase())
  )

  /* 
     With the new "link by ID" architecture, cards are saved individually when added.
     There is no "bulk save" for cards anymore in this strict separation model, 
     but we can keep the button as a "Back" or remove it. 
     For now, we'll make it just return to the main page since cards are already saved.
  */
  const handleSaveCards = async () => {
    navigate('/subscription')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Subscription not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl shadow-xl p-8 text-white">
        <button
          onClick={() => navigate('/subscription')}
          className="flex items-center text-white hover:text-gray-200 mb-4 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Subscriptions
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <CreditCard className="h-8 w-8 mr-3" />
              Add Cards to {subscription.CollectionName || subscription.collectionName}
            </h1>
            <p className="text-indigo-100">
              Subscription Amount: ₹{subscription.amount} | Current Cards: {cards.length}
            </p>
          </div>
          <button
            onClick={handleAddCard}
            className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Card
          </button>
        </div>
      </div>

      {/* Cards List */}
      <div className="space-y-4">
        {cards.length === 0 ? (
          <div className="card p-12 text-center">
            <CreditCard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cards yet</h3>
            <p className="text-gray-600 mb-4">Start by adding your first card to this subscription</p>
            <button
              onClick={handleAddCard}
              className="btn-primary mx-auto"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add First Card
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card, index) => (
              <div key={card.id} className="card">
                <div className="card-header flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Card #{index + 1}
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="relative card-action-menu">
                      <button
                        onClick={() => setActiveMenuCardId(activeMenuCardId === card.id ? null : card.id)}
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical className="h-5 w-5 text-gray-500" />
                      </button>

                      {activeMenuCardId === card.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-in fade-in zoom-in duration-100">
                          <button
                            onClick={() => {
                              handleEditCard(card)
                              setActiveMenuCardId(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Eye className="h-4 w-4 text-gray-400" />
                            Edit details
                          </button>

                          <div className="border-t border-gray-100 my-1"></div>

                          <button
                            onClick={() => {
                              handleUnlinkCard(card.id)
                              setActiveMenuCardId(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-yellow-600 hover:bg-yellow-50 flex items-center gap-2"
                          >
                            <Unlink className="h-4 w-4" />
                            Unlink from Pack
                          </button>

                          <button
                            onClick={() => {
                              handleDeleteCard(card.id)
                              setActiveMenuCardId(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete from DB
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-body space-y-3">
                  {/* Card Image */}
                  {card.imageUrl && (
                    <div className="flex justify-center bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={card.imageUrl}
                        alt={card.title || card.collectionName}
                        className="w-full h-40 object-cover border border-gray-200"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-40 text-gray-400"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>'
                        }}
                      />
                    </div>
                  )}

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Title
                    </label>
                    <p className="text-base font-semibold text-gray-900">
                      {card.title || card.collectionName}
                    </p>
                  </div>

                  {/* Test Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Test Count
                    </label>
                    <p className="text-sm text-gray-700">
                      {card.TestCount || card.testCount || 0} tests
                    </p>
                  </div>

                  {/* Discount Plan Display */}
                  {card.discountPlan && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Discount Plan
                      </label>
                      <p className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded inline-block">
                        {card.discountPlan}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {cards.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-end space-x-4">
            <button
              onClick={() => navigate('/subscription')}
              disabled={saving}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCards}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving Cards...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Done
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCancelCard} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  {isEditing ? <ImageIcon className="h-5 w-5 text-white" /> : <Plus className="h-5 w-5 text-white" />}
                </div>
                <h2 className="text-xl font-bold text-white tracking-tight">
                  {isEditing ? 'Edit Subscription Card' : 'Add New Subscription Card'}
                </h2>
              </div>
              <button
                onClick={handleCancelCard}
                className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar bg-gray-50/30">
              {/* Collection Selection Section */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Search className="h-4 w-4 text-indigo-600" />
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Select Test Package *
                  </label>
                </div>

                <div className="relative collection-select-container">
                  <div className="relative group">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-indigo-500 transition-colors" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setShowCollectionDropdown(true)
                      }}
                      onFocus={() => setShowCollectionDropdown(true)}
                      className="w-full bg-gray-50 border-gray-200 rounded-lg pl-10 pr-4 py-3 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none border"
                      placeholder="Search offline test packages..."
                    />
                  </div>

                  {showCollectionDropdown && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto overflow-x-hidden animate-in slide-in-from-top-2 duration-200">
                      {availableCollections.length === 0 ? (
                        <div className="px-6 py-8 text-center bg-gray-50">
                          <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">No packages found in database.</p>
                        </div>
                      ) : filteredCollections.length === 0 ? (
                        <div className="px-6 py-8 text-center bg-gray-50">
                          <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">No matching packages found.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-50">
                          {filteredCollections.map((collection) => {
                            const testsCount = collection.testCounts || collection.tests?.length || 0
                            return (
                              <button
                                key={collection.id}
                                type="button"
                                onClick={() => handleCollectionSelect(collection)}
                                className="w-full px-5 py-4 text-left hover:bg-indigo-50/50 flex items-center gap-4 transition-all group"
                              >
                                <div className="w-14 h-14 rounded-lg shadow-sm border border-gray-200 overflow-hidden bg-white flex-shrink-0 group-hover:scale-105 transition-transform">
                                  {collection.imageUrl ? (
                                    <img src={collection.imageUrl} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-indigo-50 flex items-center justify-center">
                                      <ImageIcon className="h-6 w-6 text-indigo-200" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-gray-900 truncate">
                                    {collection.CollectionName || collection.title || collection.id}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                      {testsCount} {testsCount === 1 ? 'Test' : 'Tests'}
                                    </span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500 italic">Click to select</span>
                                  </div>
                                </div>
                                <Plus className="h-5 w-5 text-gray-300 group-hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-all" />
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* OR Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-50 px-3 text-gray-500 font-bold">Or</span>
                </div>
              </div>

              {/* Existing Card Selection */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="h-4 w-4 text-purple-600" />
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Select Existing Card
                  </label>
                </div>

                <div className="relative">
                  <div className="relative group">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-purple-500 transition-colors" />
                    <input
                      type="text"
                      value={cardSearchTerm}
                      onChange={(e) => {
                        setCardSearchTerm(e.target.value)
                        setShowCardDropdown(true)
                      }}
                      onFocus={() => setShowCardDropdown(true)}
                      className="w-full bg-gray-50 border-gray-200 rounded-lg pl-10 pr-4 py-3 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all outline-none border"
                      placeholder="Search existing cards..."
                    />
                  </div>

                  {showCardDropdown && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto overflow-x-hidden animate-in slide-in-from-top-2 duration-200">
                      {availableCards.length === 0 ? (
                        <div className="px-6 py-8 text-center bg-gray-50">
                          <CreditCard className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">No available cards found.</p>
                          <p className="text-xs text-gray-400 mt-1">All cards are already in this subscription.</p>
                        </div>
                      ) : filteredCards.length === 0 ? (
                        <div className="px-6 py-8 text-center bg-gray-50">
                          <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">No matching cards found.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-50">
                          {filteredCards.map((card) => (
                            <button
                              key={card.id}
                              type="button"
                              onClick={() => handleCardSelect(card)}
                              className="w-full px-5 py-4 text-left hover:bg-purple-50/50 flex items-center gap-4 transition-all group"
                            >
                              <div className="w-14 h-14 rounded-lg shadow-sm border border-gray-200 overflow-hidden bg-white flex-shrink-0 group-hover:scale-105 transition-transform">
                                {card.imageUrl ? (
                                  <img src={card.imageUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-purple-50 flex items-center justify-center">
                                    <CreditCard className="h-6 w-6 text-purple-200" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-gray-900 truncate">
                                  {card.title || card.CollectionName || card.id}
                                </p>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                    {card.TestCount || card.testCount || 0} Tests
                                  </span>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs text-gray-500 font-mono">ID: {card.id}</span>
                                </div>
                              </div>
                              <Plus className="h-5 w-5 text-gray-300 group-hover:text-purple-500 opacity-0 group-hover:opacity-100 transition-all" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="space-y-4 col-span-1">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Package Index No.
                    </label>
                    <input
                      type="text"
                      value={currentCard.index}
                      readOnly
                      className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-600 font-medium cursor-not-allowed"
                      placeholder="Auto-generated"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Card Display Name *
                    </label>
                    <input
                      type="text"
                      value={currentCard.title}
                      onChange={(e) => setCurrentCard({ ...currentCard, title: e.target.value })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      placeholder="e.g., Fundamentals of Nursing"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Discount Badge / Offer
                    </label>
                    <input
                      type="text"
                      value={currentCard.discountPlan}
                      onChange={(e) => setCurrentCard({ ...currentCard, discountPlan: e.target.value })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      placeholder="e.g., 20% OFF or Festive Offer"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Test Count (Auto-fetched)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={currentCard.testCount}
                        readOnly
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-600 font-medium cursor-not-allowed"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <span className="bg-gray-200 text-gray-500 px-2 py-1 rounded text-[10px] font-bold">LOCKED</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Section */}
                <div className="col-span-1">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Card Preview Image
                  </label>

                  {currentCard.imagePreview ? (
                    <div className="relative group aspect-square max-w-[200px] mx-auto">
                      <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-white shadow-lg relative bg-gray-100">
                        <img
                          src={currentCard.imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <label className="cursor-pointer bg-white text-indigo-600 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg hover:bg-gray-100">
                            Change Image
                            <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                          </label>
                        </div>
                      </div>
                      <button
                        onClick={removeCardImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-xl border-2 border-white transition-transform active:scale-90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed border-indigo-200 rounded-2xl cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-all group">
                      <div className="bg-indigo-50 p-4 rounded-full group-hover:scale-110 transition-transform">
                        <Upload className="h-8 w-8 text-indigo-400 group-hover:text-indigo-600" />
                      </div>
                      <span className="text-sm text-indigo-600 font-bold mt-3">Upload Custom Image</span>
                      <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mt-1">JPG, PNG • High Res</span>
                      <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-indigo-50/50 p-4 rounded-xl flex gap-3 items-start border border-indigo-100">
                <div className="bg-indigo-100 p-1.5 rounded-lg shrink-0 mt-0.5">
                  <ImageIcon className="h-4 w-4 text-indigo-600" />
                </div>
                <p className="text-xs text-indigo-700 leading-relaxed font-medium">
                  <strong>Pro Tip:</strong> Select a test collection first to automatically fill in the card details. You can then customize the display name and offer badge as needed.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white px-8 py-5 border-t border-gray-100 flex items-center justify-between sticky bottom-0 z-10">
              <p className="text-xs text-gray-400 font-medium italic">
                * Required fields
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancelCard}
                  className="px-6 py-2.5 rounded-xl text-gray-500 font-bold hover:bg-gray-100 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCard}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2.5 rounded-xl font-bold shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 active:translate-y-0 text-sm flex items-center gap-2"
                >
                  {isEditing ? (
                    <>
                      <Save className="h-4 w-4" />
                      Update Changes
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Apply Card
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddSubscriptionCards
